import { useCallback, useRef, useState } from 'react'
import type { BrokerConnectionDto, OrderSideValue } from '@apna/shared'
import { tickSocket } from '@/feed/tickSocket'
import { tickStore } from '@/feed/tickStore'
import { notifyFail } from '@/components/Toast'
import type { ClickLots } from './quantity'
import type { ChainLimitSelection, ChainLimitTicket } from './chainLimitOrder'

interface TicketOptions {
  connection: BrokerConnectionDto | null
  underlying: string
  expiry: string | null
  lotSize: number
  /** How many lots this selection sends for a side — honours ₹ mode. */
  lotsFor: (selection: ChainLimitSelection, side: OrderSideValue) => ClickLots
  tickSizeOf: (token: number) => number
}

/**
 * The chain's limit-order ticket.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `open` IS STABLE FOR THE LIFE OF THE SCREEN, AND HAS TO BE
 *
 * It is handed to every `ChainRow` as `onLimit`, and `ChainRow` is `memo`'d
 * precisely so a spot tick does not redraw the ladder. The spot is React
 * state in the terminal (`useTickCell`) and an index ticks about ten times a
 * second, so a callback whose identity changed per render made every row in
 * the chain re-render ten times a second — the ladder and this ticket both
 * went visibly sluggish, reported on 2026-10-27.
 *
 * This took its options as a fresh object literal with an inline
 * `tickSizeOf`, so `useCallback`'s dependency changed on EVERY render and
 * the memo never held. The options now live in a ref that each render
 * refreshes, and `open` closes over the ref instead — so it is built once,
 * reads current values when it fires, and cannot be destabilised by a new
 * option being added later.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useChainLimitTicket(options: TicketOptions) {
  const [ticket, setTicket] = useState<ChainLimitTicket | null>(null)
  // Refreshed every render, read only when the ticket is opened.
  const latest = useRef(options)
  latest.current = options

  const open = useCallback((selection: ChainLimitSelection) => {
    const o = latest.current
    if (!o.connection || !o.expiry) { notifyFail('Choose an account and expiry first.'); return }
    setTicket({
      ...selection,
      requestId: crypto.randomUUID(),
      connection: o.connection,
      sessionId: tickSocket.getMarketContext().sessionId,
      underlying: o.underlying,
      expiry: o.expiry,
      lotSize: o.lotSize,
      tickSize: o.tickSizeOf(selection.legs[0]!.token),
      /*
       * A FUNCTION, not a number. In ₹ mode the size depends on the side —
       * a sell is sized on the margin it blocks, a buy on the premium it
       * costs — and the side is chosen inside the ticket, after this runs.
       */
      lotsFor: (side) => latest.current.lotsFor(selection, side),
      quote: selection.legs.reduce((sum, leg) => sum + (tickStore.snapshot(leg.token)?.ltp ?? 0), 0),
    })
  }, [])

  const close = useCallback(() => setTicket(null), [])
  return { ticket, open, close }
}
